import { type Dispatch, type SetStateAction, useEffect } from 'react';

import { Box, type SelectProps, Text } from '@chakra-ui/react';
import type { MenuPlacement, OptionBase } from 'chakra-react-select';
import { useTranslation } from 'react-i18next';

import { Select } from '@/components/Select';
import { useGetEmailSettingsTemplatesList } from '@/features/settings/email/queries';
import { type MailTemplateDto, type TemplateType } from '@/services/api';

interface TemplateOptions extends OptionBase {
  label: string;
  value: string | undefined;
}

type EmailTemplateSelectProps = {
  selectedTemplateId: MailTemplateDto['id'] | undefined;
  setSelectedTemplateId: Dispatch<
    SetStateAction<MailTemplateDto['id'] | undefined>
  >;
  showLabel?: boolean;
  isOptional?: boolean;
  onlySpecificType?: TemplateType;
  menuPlacement?: MenuPlacement;
  isDisabled?: boolean;
  size?: SelectProps['size'];
};

export const EmailTemplateSelect = ({
  selectedTemplateId,
  setSelectedTemplateId,
  showLabel = false,
  isOptional,
  onlySpecificType,
  menuPlacement = 'auto',
  isDisabled,
  size,
}: EmailTemplateSelectProps) => {
  const { t } = useTranslation(['components', 'common']);

  const list = useGetEmailSettingsTemplatesList();

  const toOptions = (template: MailTemplateDto): TemplateOptions => {
    return {
      label: template.title!,
      value: template.id!,
    };
  };

  useEffect(() => {
    if (list.data && !selectedTemplateId && !isOptional) {
      // Set the first template as the default if none is selected
      const defaultTemplateId = list.data[0]?.id;
      if (defaultTemplateId) {
        setSelectedTemplateId(defaultTemplateId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data, selectedTemplateId]);

  useEffect(() => {
    return () => {
      if (!isOptional) {
        setSelectedTemplateId(undefined);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box w="full">
      {showLabel && (
        <Text fontSize="sm" color="gray.700" fontWeight="medium" mb={1}>
          {t('components:emailSettings.emailTemplate')}
        </Text>
      )}
      <Select
        isDisabled={isDisabled}
        isLoading={list.isLoading}
        value={list.data
          ?.map(toOptions)
          .find((template) => template.value === selectedTemplateId)}
        onChange={(template) => {
          if (template) setSelectedTemplateId(template.value);
        }}
        options={[
          ...(list.data
            ?.filter((o) =>
              onlySpecificType ? o.type === onlySpecificType : true
            )
            ?.map(toOptions) ?? []),
        ]}
        placeholder={t('common:actions.select')}
        menuPlacement={menuPlacement}
        size={size as ExplicitAny}
      />
    </Box>
  );
};
